import { useState } from "react";
import { CalendarDays, ChevronDown } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { useMe } from "@/lib/me";
import { presets, rangeLabel } from "@/lib/dates";

const toDate = (s) => (s ? new Date(`${s}T00:00:00`) : undefined);
const iso = (d) => d.toLocaleDateString("en-CA");

// value: { key, from, to } — key is a preset key or "custom".
export default function DateRange({ value, onChange, testid = "date-range", className = "" }) {
  const me = useMe();
  const [open, setOpen] = useState(false);
  const [cal, setCal] = useState(false);
  const list = presets(me?.settings?.year_start_month || 4);
  const groups = [...new Set(list.map((p) => p.group))];
  const cur = list.find((p) => p.key === value.key);
  const pick = (p) => { onChange({ key: p.key, from: p.from, to: p.to }); setOpen(false); };
  const onCal = (r) => {
    if (!r?.from) return;
    onChange({ key: "custom", from: iso(r.from), to: iso(r.to || r.from) });
  };
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setCal(false); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" className={`h-9 justify-between gap-2 bg-white font-normal ${className}`} data-testid={`${testid}-trigger`}>
          <span className="flex min-w-0 items-center gap-2">
            <CalendarDays className="h-4 w-4 shrink-0 text-teal-700" />
            <span className="truncate"><b className="font-semibold">{cur ? cur.label.split(" (")[0] : "Custom"}</b>
              <span className="ml-1.5 text-slate-500">{value.key === "all" ? "" : rangeLabel(value)}</span></span>
          </span>
          <ChevronDown className="h-4 w-4 shrink-0 text-slate-400" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" collisionPadding={8} className="w-[min(94vw,640px)] p-0" data-testid={`${testid}-popover`}>
        <div className="flex flex-col sm:flex-row">
          <div className={`max-h-[60vh] overflow-y-auto p-2 sm:block sm:max-h-[22rem] sm:w-60 sm:border-r sm:border-slate-100 ${cal ? "hidden" : ""}`}>
            <button type="button" onClick={() => setCal(true)} className="mb-1 w-full rounded-lg border border-dashed border-slate-300 px-2 py-2 text-left text-sm font-semibold text-teal-700 sm:hidden" data-testid={`${testid}-custom-open`}>
              Custom range…
            </button>
            {groups.map((g) => (
              <div key={g} className="mb-1">
                <p className="px-2 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{g}</p>
                {list.filter((p) => p.group === g).map((p) => (
                  <button key={p.key} type="button" onClick={() => pick(p)} data-testid={`${testid}-preset-${p.key}`}
                    className={`block w-full rounded-lg px-2 py-1.5 text-left text-sm transition-colors ${value.key === p.key ? "bg-slate-900 text-white" : "hover:bg-slate-100"}`}>
                    {p.label}
                  </button>
                ))}
              </div>
            ))}
          </div>
          <div className={`flex-1 p-1 sm:block ${cal ? "" : "hidden"}`} data-testid={`${testid}-calendar`}>
            <p className="px-3 pt-2 text-xs font-semibold text-slate-500">Custom range — tap a start and an end day</p>
            <Calendar mode="range" weekStartsOn={1} selected={{ from: toDate(value.from), to: toDate(value.to) }} onSelect={onCal}
              defaultMonth={toDate(value.from) || new Date()} />
            <div className="flex justify-between px-3 pb-3 sm:justify-end">
              <Button size="sm" variant="ghost" className="sm:hidden" onClick={() => setCal(false)} data-testid={`${testid}-back`}>Quick options</Button>
              <Button size="sm" onClick={() => { setOpen(false); setCal(false); }} data-testid={`${testid}-done`}>Done</Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
